import { createPortal } from 'react-dom';
import { useHeaderActionsTarget } from '@/layouts/usePageTitle';
import HomeTagFilter from '@/components/dashboard/HomeTagFilter';
import { useLinkedPlantTags } from '@/hooks/useLinkedPlantTags';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import { useEffect, useMemo, useRef, useState } from 'react';
import InvestorAIProviderSelect from '@/components/investor/InvestorAIProviderSelect';
import { Link } from 'react-router-dom';
import { Plus, Save, Search, X } from 'lucide-react';
import ColumnValuesFilter from '@/components/common/ColumnValuesFilter';
import { AgGridReact } from 'ag-grid-react';
import {
  colorSchemeLightCold,

  themeQuartz,
} from 'ag-grid-community';
import type { ColDef } from 'ag-grid-community';
import toast from 'react-hot-toast';
import { fetchInvestors, createInvestor, type Investor, type InvestorUpdatePayload } from '@/api/investors';
import { useI18n } from '@/i18n';
import { useErrorToast } from '@/lib/notify';

const modules = [AllEnterpriseModule];
const formatProduction = (value: unknown) => {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  const numeric = Number(value);
  const magnitude = Math.abs(numeric);
  const divisor = magnitude >= 1_000_000 ? 1_000_000 : magnitude >= 1000 ? 1000 : 1;
  const unit = divisor === 1_000_000 ? 'GWh' : divisor === 1000 ? 'MWh' : 'kWh';
  return `${(numeric / divisor).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${unit}`;
};
const productionColumn: ColDef<Investor> = {
  type: 'numericColumn',
  cellClass: 'ag-right-aligned-cell tabular-nums',
  headerClass: 'ag-right-aligned-header',
  valueFormatter: ({ value }) => formatProduction(value),
  filter: ColumnValuesFilter,
  filterParams: { valueLabel: formatProduction },
};

const theme = themeQuartz.withPart(colorSchemeLightCold).withParams({
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 11.5,
  rowHeight: 32,
  headerHeight: 32,
  borderRadius: 6,
  headerBackgroundColor: '#f8fafc',
  borderColor: '#e2e8f0',
  oddRowBackgroundColor: '#f8fafc',
});

function StatusBadge({ status }: { status: number }) {
  const { t } = useI18n();
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
        status === 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
      }`}
    >
      {status === 1 ? t('investors.active') : t('investors.passive')}
    </span>
  );
}

function NewInvestorModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (uuid_id: string) => void;
}) {
  const { t } = useI18n();
  const [form, setForm] = useState<InvestorUpdatePayload>({
    investor_name: '',
    manager_name: '',
    manager_surname: '',
    email: '',
    phone: '',
    address: '',
    subdomain: '',
    ai_provider:'gemini',
    status: 1,
  });
  const [saving, setSaving] = useState(false);

  const set = (key: keyof InvestorUpdatePayload, value: string | number) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  async function handleSave() {
    if (!form.investor_name.trim()) {
      toast.error(t('investors.nameRequired'));
      return;
    }
    setSaving(true);
    try {
      const { uuid_id } = await createInvestor(form);
      toast.success(t('investors.createSuccess'));
      onCreated(uuid_id);
    } catch {
      toast.error(t('investors.createError'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="max-h-[90dvh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between border-b pb-3">
          <h3 className="text-lg font-semibold text-gray-900">{t('investors.newTitle')}</h3>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs font-medium text-gray-500">{t('investors.name')}</label>
            <input
              type="text"
              value={form.investor_name}
              onChange={(e) => set('investor_name', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">{t('investors.managerName')}</label>
            <input
              type="text"
              value={form.manager_name}
              onChange={(e) => set('manager_name', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">{t('investors.managerSurname')}</label>
            <input
              type="text"
              value={form.manager_surname}
              onChange={(e) => set('manager_surname', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">{t('investors.email')}</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">{t('investors.phone')}</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs font-medium text-gray-500">Subdomain</label>
            <input
              type="text"
              value={form.subdomain}
              onChange={(e) => set('subdomain', e.target.value)}
              placeholder="ornek-yatirimci"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
            <p className="mt-1 text-xs text-gray-400">Örn: ornek-yatirimci.pvsense.local</p>
          </div>
          <InvestorAIProviderSelect value={form.ai_provider} onChange={value=>set('ai_provider',value)}/>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs font-medium text-gray-500">{t('investors.address')}</label>
            <textarea
              value={form.address}
              onChange={(e) => set('address', e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs font-medium text-gray-500">{t('common.status')}</label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => set('status', 1)}
                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                  form.status === 1
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                    : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                }`}
              >
                {t('investors.active')}
              </button>
              <button
                type="button"
                onClick={() => set('status', 0)}
                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                  form.status === 0
                    ? 'border-rose-500 bg-rose-50 text-rose-700'
                    : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                }`}
              >
                {t('investors.passive')}
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3 border-t pt-4">
          <button
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-50"
          >
            {t('investors.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:opacity-50"
          >
            <Save size={16} />
            {t('investors.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function InvestorsPage() {
  const { t } = useI18n();
  const headerActionsTarget = useHeaderActionsTarget();
  const gridRef = useRef<AgGridReact<Investor>>(null);
  const [rowData, setRowData] = useState<Investor[]>([]);
  const {availableTags,selectedTagIds,setSelectedTagIds,tagFilteredRows}=useLinkedPlantTags(rowData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useErrorToast(error);
  const [newOpen, setNewOpen] = useState(false);
  const [search, setSearch] = useState('');

  function loadInvestors() {
    setLoading(true);
    setError('');
    fetchInvestors()
      .then(data => { setRowData(data); })
      .catch(() => setError(t('investors.loadError')))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadInvestors();
  }, [t]);

  const colDefs = useMemo<ColDef<Investor>[]>(
    () => [
      {
        headerName: '',
        field: 'logo_url',
        width: 60,
        pinned: 'left',
        sortable: false,
        filter: false,
        suppressMovable: true,
        cellRenderer: ({ value }: { value: string | null }) => (
          <div className="flex h-full items-center justify-center">
            {value ? (
              <div className="h-8 w-8 overflow-hidden rounded-lg border border-gray-100 bg-white shadow-xs">
                <img src={value} alt="Logo" className="h-full w-full object-contain" />
              </div>
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-50 text-gray-300 border border-gray-100">
                <Plus size={12} className="opacity-40" />
              </div>
            )}
          </div>
        ),
      },
      {
        field: 'investor_name',
        headerName: t('investors.name'),
        cellRenderer: ({ data }: { data?: Investor }) => data ? (
          <Link to={`/dashboard/investor/${data.investor_uuid_id}/detail`} className="font-medium text-blue-600 hover:text-blue-800 hover:underline">{data.investor_name}</Link>
        ) : null,
        flex: 2,
        minWidth: 180,
        pinned: 'left',
        filter: ColumnValuesFilter,
      },
      {
        field: 'investor_manager_fullname',
        headerName: t('investors.manager'),
        flex: 1.5,
        minWidth: 180,
        filter: ColumnValuesFilter,
        valueFormatter: ({ value }: { value: string | null }) => value || '—',
      },
      {
        field: 'subdomain',
        headerName: 'Subdomain',
        flex: 1,
        minWidth: 160,
        filter: ColumnValuesFilter,
        valueFormatter: ({ value }: { value: string | null }) => value || '—',
      },
      {
        field: 'plant_count',
        headerName: t('investors.plantCount'),
        width: 130,
        filter: ColumnValuesFilter,
      },
      {
        ...productionColumn,
        field: 'investor_power_plant_e_day',
        headerName: t('common.eDay'),
        flex: 1,
        minWidth: 120,
        filter: ColumnValuesFilter,
      },
      {
        ...productionColumn,
        field: 'investor_power_plant_e_total',
        headerName: t('common.eTotal'),
        flex: 1,
        minWidth: 120,
        filter: ColumnValuesFilter,
      },
      {
        field: 'email',
        headerName: t('investors.email'),
        flex: 1.7,
        minWidth: 220,
        filter: ColumnValuesFilter,
        valueFormatter: ({ value }: { value: string | null }) => value || '—',
      },
      {
        field: 'status',
        headerName: t('common.status'),
        width: 110,
        filter: ColumnValuesFilter,
        filterValueGetter: params => params.data?.status === 1 ? t('investors.active') : t('investors.passive'),
        getQuickFilterText: params => params.value === 1 ? t('investors.active') : t('investors.passive'),
        cellRenderer: ({ value }: { value: number }) => (
          <StatusBadge status={value} />
        ),
      },
    ],
    [t],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      floatingFilter: false,
      suppressHeaderMenuButton: false,
    }),
    [],
  );

  function scrollToInvestor(uuid_id: string) {
    const api = gridRef.current?.api;
    if (!api) return;
    let rowIndex = -1;
    api.forEachNode((node, idx) => {
      if (node.data?.investor_uuid_id === uuid_id) rowIndex = idx;
    });
    if (rowIndex >= 0) {
      api.ensureIndexVisible(rowIndex, 'middle');
      api.getDisplayedRowAtIndex(rowIndex)?.setSelected(true);
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {headerActionsTarget && createPortal(<div className="flex w-full min-w-0 flex-wrap items-center justify-start gap-1.5 xl:justify-end">
        <label className="flex items-center h-8 gap-1.5 rounded-lg border border-slate-200 px-2 text-slate-400"><Search size={14} /><input aria-label="Yatırımcı listesinde ara" placeholder="Yatırımcı, yönetici veya e-posta ara…" value={search} onChange={event => setSearch(event.target.value)} className="w-32 bg-transparent text-xs text-slate-700 outline-none xl:w-40" /></label>

        <HomeTagFilter tags={availableTags} selectedIds={selectedTagIds} onChange={setSelectedTagIds} title="Bağlı santral etiketleri" allLabel="Tüm yatırımcılar göster" emptyLabel="Tüm yatırımcılar"/>
        <button
              type="button"
              onClick={() => setNewOpen(true)}
              className="flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 h-8 px-3 text-xs font-medium text-white transition hover:bg-blue-700"
            >
              <Plus size={14} />
              {t('investors.new')}
            </button>
      </div>,headerActionsTarget)}


      <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="min-h-0 flex-1 [&_.ag-paging-row-summary-panel]:hidden">
        <AgGridReact<Investor>
          ref={gridRef}
          modules={modules}
          theme={theme}
          rowData={tagFilteredRows}
          quickFilterText={search}
          getRowId={params => params.data.investor_uuid_id}
          columnDefs={colDefs}
          defaultColDef={defaultColDef}
          loading={loading}
          animateRows
          rowSelection="single"
          enableCellTextSelection
          pagination
          paginationPageSize={100}
          paginationPageSizeSelector={[25, 50, 100]}
          tooltipShowDelay={250}
          overlayNoRowsTemplate="<span>Bu filtrelere uygun yatırımcı bulunamadı.</span>"
        />
        </div>
      </section>

      {newOpen && (
        <NewInvestorModal
          onClose={() => setNewOpen(false)}
          onCreated={(uuid_id) => {
            setNewOpen(false);
            setSearch('');
            loadInvestors();
            setTimeout(() => scrollToInvestor(uuid_id), 500);
          }}
        />
      )}
    </div>
  );
}
